import { useReducer } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { HideTodoDialog } from "./HideTodoDialog";
import {
  customerStateOf,
  CustomerState,
  TodoCustomerPicker,
  useSaveTodoCustomer,
  useTodoCustomerOptions,
  withCustomer,
} from "./TodoCustomerPicker";
import { TodoContextLine } from "./TodoContextLine";
import {
  Activity,
  UpdateCustomerActivityInput,
  useApproveTodo,
  useGetTodoTranscript,
  useUpdateActivity,
} from "@/lib/api/customer";

export function useTodoTranscript(todoId: string, enabled: boolean) {
  const transcriptQuery = useGetTodoTranscript(todoId, enabled);
  const transcriptResult = transcriptQuery.data;
  const transcript = transcriptResult?.success
    ? transcriptResult.data.transcript
    : null;
  const transcriptError = transcriptQuery.isError
    ? "An unexpected error occurred while loading the transcript."
    : transcriptResult && !transcriptResult.success
      ? transcriptResult.message || "The transcript could not be loaded."
      : null;

  return { transcriptQuery, transcript, transcriptError };
}

export function TodoTranscriptContent({
  transcriptQuery,
  transcript,
  transcriptError,
}: ReturnType<typeof useTodoTranscript>) {
  if (transcriptQuery.isLoading) {
    return (
      <div className="flex min-h-48 items-center justify-center">
        <span className="text-xs text-muted-foreground">Loading...</span>
      </div>
    );
  }

  if (transcriptError) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        <p>{transcriptError}</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={() => transcriptQuery.refetch()}
          disabled={transcriptQuery.isFetching}
        >
          {transcriptQuery.isFetching ? "Retrying..." : "Try Again"}
        </Button>
      </div>
    );
  }

  if (transcript === null) return null;

  if (!transcript.trim()) {
    return (
      <div className="flex min-h-48 items-center justify-center rounded-md bg-muted/20 p-4 text-sm text-muted-foreground">
        This transcript is empty.
      </div>
    );
  }

  return (
    <ScrollArea className="min-h-0 flex-1 rounded-md bg-muted/20 p-4">
      <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed">
        {transcript}
      </pre>
    </ScrollArea>
  );
}

interface TodoTranscriptDialogProps {
  todo: Activity | null;
  onClose: () => void;
  /** Show the customer picker and the Hide and Approve buttons. */
  showReviewActions?: boolean;
}

type PendingAction = "customer" | "approve";

interface DialogState {
  // Local copy of the saved text so Save/Cancel stay correct until the list
  // refetches.
  savedContent: string;
  draft: string;
  // Same for the customer: the parent's snapshot goes stale once it is changed here.
  customer: CustomerState;
  selectedCustomerId: string;
  isSaving: boolean;
  pendingAction: PendingAction | null;
  isDiscardDialogOpen: boolean;
  isHideDialogOpen: boolean;
}

type DialogAction =
  | { type: "draftChanged"; draft: string }
  | { type: "draftReverted" }
  | { type: "saveStarted" }
  | { type: "saveFinished" }
  | { type: "contentSaved" }
  | { type: "customerSelected"; customerId: string }
  | { type: "customerSaved"; customer: CustomerState }
  | { type: "actionStarted"; action: PendingAction }
  | { type: "actionFinished" }
  | { type: "hideDialogToggled"; isOpen: boolean }
  | { type: "discardPromptToggled"; isOpen: boolean }
  | { type: "changesDiscarded" };

function initialStateOf(todo: Activity | null): DialogState {
  const content = todo?.content ?? "";
  const customer = customerStateOf(todo);
  return {
    savedContent: content,
    draft: content,
    customer,
    selectedCustomerId: customer.id,
    isSaving: false,
    pendingAction: null,
    isDiscardDialogOpen: false,
    isHideDialogOpen: false,
  };
}

function dialogReducer(state: DialogState, action: DialogAction): DialogState {
  switch (action.type) {
    case "draftChanged":
      return { ...state, draft: action.draft };
    case "draftReverted":
      return { ...state, draft: state.savedContent };
    case "saveStarted":
      return { ...state, isSaving: true };
    case "saveFinished":
      return { ...state, isSaving: false };
    case "contentSaved":
      return { ...state, savedContent: state.draft };
    case "customerSelected":
      return { ...state, selectedCustomerId: action.customerId };
    case "customerSaved":
      return {
        ...state,
        customer: action.customer,
        selectedCustomerId: action.customer.id,
      };
    case "actionStarted":
      return { ...state, pendingAction: action.action };
    case "actionFinished":
      return { ...state, pendingAction: null };
    case "hideDialogToggled":
      return { ...state, isHideDialogOpen: action.isOpen };
    case "discardPromptToggled":
      return { ...state, isDiscardDialogOpen: action.isOpen };
    case "changesDiscarded":
      return {
        ...state,
        draft: state.savedContent,
        selectedCustomerId: state.customer.id,
        isDiscardDialogOpen: false,
      };
  }
}

// Keying on the todo id remounts the dialog, which resets its state when a
// different todo is opened.
export function TodoTranscriptDialog({
  todo,
  onClose,
  showReviewActions,
}: TodoTranscriptDialogProps) {
  return (
    <TodoTranscriptDialogContent
      key={todo?.id ?? "closed"}
      todo={todo}
      onClose={onClose}
      showReviewActions={showReviewActions}
    />
  );
}

function TodoTranscriptDialogContent({
  todo,
  onClose,
  showReviewActions,
}: TodoTranscriptDialogProps) {
  const { toast } = useToast();
  const updateActivity = useUpdateActivity();
  const approveTodo = useApproveTodo();
  const { customerOptions, isLoading: isLoadingCustomers } = useTodoCustomerOptions();
  const saveTodoCustomer = useSaveTodoCustomer(customerOptions);
  const isOpen = todo !== null;
  const transcriptState = useTodoTranscript(todo?.id ?? "", isOpen);

  const [state, dispatch] = useReducer(dialogReducer, todo, initialStateOf);
  const {
    savedContent,
    draft,
    customer,
    selectedCustomerId,
    isSaving,
    pendingAction,
    isDiscardDialogOpen,
    isHideDialogOpen,
  } = state;

  const isEditable = !!todo && !todo.approved_date && !todo.hidden_date;
  const isBusy = isSaving || pendingAction !== null;
  const hasUnsavedText = draft !== savedContent;
  const canChangeCustomer = !!showReviewActions && isEditable;
  const hasUnsavedCustomer = canChangeCustomer && selectedCustomerId !== customer.id;
  const hasUnsavedChanges = hasUnsavedText || hasUnsavedCustomer;
  const canSave = isEditable && hasUnsavedText && draft.trim().length > 0 && !isBusy;
  const canHide = !todo?.hidden_date;
  const canApprove = !todo?.approved_date && !todo?.hidden_date;
  const currentTodo = withCustomer(todo, customer);

  const closeWithoutConfirmation = () => {
    dispatch({ type: "changesDiscarded" });
    onClose();
  };

  const requestClose = () => {
    if (isBusy) return;

    if (hasUnsavedChanges) {
      dispatch({ type: "discardPromptToggled", isOpen: true });
      return;
    }

    closeWithoutConfirmation();
  };

  const handleSave = async () => {
    if (!todo || !canSave) return;

    dispatch({ type: "saveStarted" });

    try {
      // This route rewrites status and assignment from the request, so send the
      // todo's current values to leave them as they are.
      const input = UpdateCustomerActivityInput.safeParse({
        id: todo.id,
        resource_type: "TODO",
        customer_id: customer.id,
        content: draft,
        status: todo.status || "TODO",
        assigned_to_id: todo.last_assigned_to_id || undefined,
      });

      if (!input.success) {
        toast({ title: "Unable to save Todo", description: "The Todo text is invalid.", variant: "destructive" });
        return;
      }

      const result = await updateActivity(input.data);

      if (!result.success) {
        toast({
          title: "Unable to save Todo",
          description: result.message,
          variant: "destructive",
        });
        return;
      }

      dispatch({ type: "contentSaved" });
      toast({ title: "Todo updated", description: "Your changes were saved." });
    } catch {
      toast({
        title: "Unable to save Todo",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      dispatch({ type: "saveFinished" });
    }
  };

  const handleChangeCustomer = async () => {
    if (!todo || !hasUnsavedCustomer || !selectedCustomerId || isBusy) return;

    dispatch({ type: "actionStarted", action: "customer" });

    try {
      const saved = await saveTodoCustomer(todo.id, selectedCustomerId);
      if (saved) dispatch({ type: "customerSaved", customer: saved });
    } finally {
      dispatch({ type: "actionFinished" });
    }
  };

  const handleApprove = async () => {
    if (!todo || isBusy || hasUnsavedChanges) return;

    dispatch({ type: "actionStarted", action: "approve" });

    try {
      const result = await approveTodo({ todo_id: todo.id });

      if (!result.success) {
        toast({
          title: "Unable to approve Todo",
          description: result.message,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Todo approved",
        description: "The Todo now appears in the To-Dos list.",
      });
      closeWithoutConfirmation();
    } catch {
      toast({
        title: "Unable to approve Todo",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      dispatch({ type: "actionFinished" });
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) requestClose();
        }}
      >
        <DialogContent className="flex h-[calc(100vh-5vw)] w-[95vw] max-w-[95vw] flex-col sm:h-[calc(100vh-10vw)] sm:max-w-[90vw]">
          <DialogHeader>
            <DialogTitle>Call Transcript</DialogTitle>
            <TodoContextLine todo={currentTodo} />
            <DialogDescription>
              Review the call this Todo was created from. The Todo can be edited
              only while it needs review.
            </DialogDescription>
          </DialogHeader>
          {(canChangeCustomer || (showReviewActions && (canHide || canApprove))) && (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {canChangeCustomer && (
                <TodoCustomerPicker
                  id="reviewTodoCustomer"
                  testId="review-todo-change-customer"
                  customerOptions={customerOptions}
                  isLoading={isLoadingCustomers}
                  customerId={customer.id}
                  selectedCustomerId={selectedCustomerId}
                  onSelect={(customerId) =>
                    dispatch({ type: "customerSelected", customerId })
                  }
                  onSave={handleChangeCustomer}
                  isSaving={pendingAction === "customer"}
                  isDisabled={isBusy}
                />
              )}
              {showReviewActions && (canHide || canApprove) && (
                <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                  {hasUnsavedChanges && (
                    <span className="text-xs text-muted-foreground">
                      {hasUnsavedText
                        ? "Save or cancel the Todo edit first."
                        : "Save or cancel the customer change first."}
                    </span>
                  )}
                  {canHide && (
                    <Button
                      type="button"
                      variant="destructive"
                      data-testid="review-todo-hide"
                      onClick={() =>
                        dispatch({ type: "hideDialogToggled", isOpen: true })
                      }
                      disabled={isBusy || hasUnsavedChanges}
                    >
                      Hide
                    </Button>
                  )}
                  {canApprove && (
                    <Button
                      type="button"
                      data-testid="review-todo-approve"
                      onClick={handleApprove}
                      disabled={isBusy || hasUnsavedChanges}
                    >
                      {pendingAction === "approve" ? "Approving..." : "Approve"}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto md:flex-row md:overflow-visible">
            <div className="flex min-h-[16rem] min-w-0 flex-1 flex-col">
              <TodoTranscriptContent {...transcriptState} />
            </div>
            <div className="flex min-h-[16rem] flex-col gap-2 md:w-2/5 md:max-w-xl md:shrink-0">
              <label htmlFor="todo-edit-content" className="text-sm font-medium">
                Todo
              </label>
              <Textarea
                id="todo-edit-content"
                data-testid="todo-edit-content"
                value={draft}
                onChange={(event) =>
                  dispatch({ type: "draftChanged", draft: event.target.value })
                }
                className="min-h-0 flex-1 resize-none"
                readOnly={!isEditable}
                disabled={isBusy}
              />
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="todo-edit-cancel"
                  onClick={() => dispatch({ type: "draftReverted" })}
                  disabled={!isEditable || !hasUnsavedText || isBusy}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  data-testid="todo-edit-save"
                  onClick={handleSave}
                  disabled={!canSave}
                >
                  {isSaving ? "Saving..." : "Save"}
                </Button>
              </div>
              {!isEditable && (
                <p className="text-xs text-muted-foreground">
                  Approved or hidden Todos cannot be edited.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={requestClose}
              disabled={isBusy}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <HideTodoDialog
        todo={isHideDialogOpen ? currentTodo : null}
        onClose={() => dispatch({ type: "hideDialogToggled", isOpen: false })}
        onHidden={closeWithoutConfirmation}
      />

      <Dialog
        open={isDiscardDialogOpen}
        onOpenChange={(isOpen) =>
          dispatch({ type: "discardPromptToggled", isOpen })
        }
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Discard unsaved changes?
            </DialogTitle>
            <DialogDescription>
              {hasUnsavedText && hasUnsavedCustomer
                ? "Your edits to this Todo and your customer change have not been saved."
                : hasUnsavedCustomer
                  ? "Your customer change has not been saved."
                  : "Your edits to this Todo have not been saved."}{" "}
              Discarding cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                dispatch({ type: "discardPromptToggled", isOpen: false })
              }
            >
              Keep Editing
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={closeWithoutConfirmation}
            >
              Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
